import { DecimalPipe } from '@angular/common';
import {
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Auth, user } from '@angular/fire/auth';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { Subscription } from 'rxjs';
import {
  Booking,
  SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS,
  SECURITY_DEPOSIT_METHOD_LABELS,
  SecurityDepositReceivedMethod,
  bookingNeedsOwnerAction,
  isSecurityDepositSecured,
} from '../../models/booking.model';
import { Asset } from '../../models/asset.model';
import { AssetService } from '../../services/asset.service';
import { BookingService } from '../../services/booking.service';
import {
  isFullPaymentBooking,
  ownerBalanceEurosFromBooking,
  rentalCentsFromBooking,
} from '../../shared/booking/booking-amounts.utils';

export type OwnerActionTab = 'pending' | 'verify' | 'confirmed' | 'active';

const TAB_STATUS: Record<OwnerActionTab, Booking['status']> = {
  pending:   'PENDING_FORMALIZATION',
  verify:    'PENDING_VERIFICATION',
  confirmed: 'CONFIRMED',
  active:    'ACTIVE',
};

export interface OwnerBookingRow extends Booking {
  id: string;
}

type ConfirmModalKind = 'reject' | 'returnDeposit' | 'captureDeposit' | 'activateWithoutDeposit' | 'completeEvent';

interface ConfirmModalState {
  kind: ConfirmModalKind;
  booking: OwnerBookingRow;
}

interface ConfirmModalCopy {
  title: string;
  body: string;
  confirmLabel: string;
  destructive: boolean;
}

@Component({
  selector: 'app-owner-manage-bookings',
  standalone: true,
  imports: [LucideAngularModule, DecimalPipe],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './owner-manage-bookings.component.html',
})
export class OwnerManageBookingsComponent implements OnDestroy {
  private auth = inject(Auth);
  private bookingService = inject(BookingService);
  private assetService = inject(AssetService);
  private sanitizer = inject(DomSanitizer);

  private bookingsSub?: Subscription;
  private assetsSub?: Subscription;

  readonly authUser = toSignal(user(this.auth));
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly allBookings = signal<OwnerBookingRow[]>([]);
  readonly spaces = signal<Asset[]>([]);

  readonly selectedSpaceId = signal<string>('all');
  readonly activeTab = signal<OwnerActionTab>('verify');

  readonly previewUrl = signal<string | null>(null);
  readonly previewIsPdf = signal(false);
  readonly previewSafeUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.previewUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });
  readonly actionBusyId = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);

  readonly depositModalBooking = signal<OwnerBookingRow | null>(null);
  readonly depositMethodSelection = signal<SecurityDepositReceivedMethod | null>(null);
  readonly confirmModal = signal<ConfirmModalState | null>(null);
  /** Optional owner message when rejecting a deposit receipt. */
  readonly rejectNote = signal('');
  readonly rejectNoteMaxLength = 500;
  /** Amount the owner wants to capture (string input, validated before use). */
  readonly captureAmountEur = signal('');
  readonly captureAmountCents = computed(() => {
    const v = parseFloat(this.captureAmountEur().replace(',', '.'));
    return isNaN(v) || v <= 0 ? 0 : Math.round(v * 100);
  });
  readonly captureAmountValid = computed(() => {
    const modal = this.confirmModal();
    if (modal?.kind !== 'captureDeposit') return true;
    const cents = this.captureAmountCents();
    const max = modal.booking.depositCents ?? 0;
    return cents >= 50 && cents <= max;
  });

  readonly confirmModalCopy = computed((): ConfirmModalCopy | null => {
    const modal = this.confirmModal();
    if (!modal) return null;
    switch (modal.kind) {
      case 'reject':
        return {
          title: 'Justificante incorrecto',
          body: 'La reserva volverá a formalización. El cliente recibirá un aviso para subir de nuevo el justificante de la señal. El hueco seguirá bloqueado hasta que lo corrija o expire el plazo.',
          confirmLabel: 'Devolver a formalización',
          destructive: false,
        };
      case 'returnDeposit':
        return modal.booking.securityDepositMethod === 'card_hold'
          ? {
              title: 'Liberar retención',
              body: 'Se cancelará la autorización en la tarjeta del cliente sin cobrar nada. La reserva pasará a completada.',
              confirmLabel: 'Liberar retención',
              destructive: false,
            }
          : {
              title: 'Devolver fianza',
              body: 'Confirma que has devuelto la fianza al cliente. La reserva pasará a completada.',
              confirmLabel: 'Confirmar devolución',
              destructive: false,
            };
      case 'captureDeposit': {
        const maxEur = ((modal.booking.depositCents ?? 0) / 100).toFixed(2);
        return {
          title: 'Cobrar fianza',
          body: `Introduce el importe a cobrar (máximo ${maxEur} €). Si cobras un importe parcial, el resto se libera automáticamente en la tarjeta del cliente.`,
          confirmLabel: 'Cobrar fianza',
          destructive: false,
        };
      }
      case 'activateWithoutDeposit':
        return {
          title: 'Activar sin fianza acreditada',
          body: 'El cliente aún no ha completado la fianza en la plataforma. Si activas igualmente, no podrás registrar la devolución de fianza después. Quedará registrado en la reserva.',
          confirmLabel: 'Activar sin fianza',
          destructive: true,
        };
      case 'completeEvent':
        return {
          title: 'Finalizar evento',
          body: 'La fianza no se gestionó por la plataforma. La reserva pasará a completada sin paso de devolución.',
          confirmLabel: 'Finalizar',
          destructive: false,
        };
    }
  });

  readonly depositMethodOptions: { id: SecurityDepositReceivedMethod; label: string }[] = [
    { id: 'transfer', label: SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS.transfer },
    { id: 'bizum', label: SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS.bizum },
    { id: 'cash', label: SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS.cash },
  ];

  readonly spaceOptions = computed(() => {
    const list = this.spaces();
    return [
      { id: 'all', label: 'Todos los locales' },
      ...list.map(s => ({
        id: s.id ?? '',
        label: s.name?.trim() || 'Sin nombre',
      })).filter(o => o.id),
    ];
  });

  readonly spaceNameById = computed(() => {
    const map = new Map<string, string>();
    for (const s of this.spaces()) {
      if (s.id) map.set(s.id, s.name?.trim() || 'Espacio');
    }
    return map;
  });

  readonly spaceFilteredActionable = computed(() => {
    const spaceId = this.selectedSpaceId();
    return this.allBookings()
      .filter(b => bookingNeedsOwnerAction(b))
      .filter(b => spaceId === 'all' || b.spaceId === spaceId);
  });

  readonly tabCounts = computed(() => {
    const rows = this.spaceFilteredActionable();
    return {
      pending:   rows.filter(b => b.status === 'PENDING_FORMALIZATION').length,
      verify:    rows.filter(b => b.status === 'PENDING_VERIFICATION').length,
      confirmed: rows.filter(b => b.status === 'CONFIRMED').length,
      active:    rows.filter(b => b.status === 'ACTIVE').length,
    };
  });

  readonly visibleBookings = computed(() => {
    const status = TAB_STATUS[this.activeTab()];
    return this.spaceFilteredActionable()
      .filter(b => b.status === status)
      .sort((a, b) => {
        const dateCmp = a.date.localeCompare(b.date);
        return dateCmp !== 0 ? dateCmp : a.startTime.localeCompare(b.startTime);
      });
  });

  readonly actionsRequiredCount = computed(() => this.spaceFilteredActionable().length);

  readonly activeTabHint = computed(() => {
    const hints: Record<OwnerActionTab, string> = {
      pending:   'El cliente ha pagado y está pendiente de firmar el contrato. No se requiere acción por tu parte.',
      verify:    'Revisa transferencias y Bizum en tu banco antes de confirmar.',
      confirmed: 'Registra la fianza en el local y activa el día del evento.',
      active:    'Devuelve la fianza o finaliza el evento si activaste sin fianza acreditada.',
    };
    return hints[this.activeTab()];
  });

  readonly actionsRequiredLabel = computed(() => {
    const n = this.actionsRequiredCount();
    if (n === 0) return 'Sin acciones pendientes';
    return n === 1 ? '1 acción requerida' : `${n} acciones requeridas`;
  });

  readonly tabs: { id: OwnerActionTab; label: string }[] = [
    { id: 'pending',   label: 'Pendiente firma' },
    { id: 'verify',    label: 'Por verificar' },
    { id: 'confirmed', label: 'Confirmadas' },
    { id: 'active',    label: 'Activas' },
  ];

  readonly shellClasses = 'tfr-page-shell py-5 lg:py-10 overflow-x-hidden';

  constructor() {
    effect(() => {
      const authState = this.authUser();

      this.bookingsSub?.unsubscribe();
      this.assetsSub?.unsubscribe();

      if (authState === undefined) {
        this.loading.set(true);
        this.loadError.set(false);
        return;
      }

      const uid = authState?.uid;
      if (!uid) {
        this.loading.set(false);
        this.loadError.set(true);
        this.allBookings.set([]);
        return;
      }

      this.loading.set(true);
      this.loadError.set(false);

      this.bookingsSub = this.bookingService.watchOwnerBookings(uid).subscribe({
        next: rows => {
          this.allBookings.set(
            rows.filter((b): b is OwnerBookingRow => typeof b.id === 'string' && b.id.length > 0),
          );
          this.loading.set(false);
          this.loadError.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.loadError.set(true);
        },
      });

      this.assetsSub = this.assetService.getAssetsByOwner(uid).subscribe({
        next: assets => this.spaces.set(assets),
      });
    });
  }

  ngOnDestroy(): void {
    this.bookingsSub?.unsubscribe();
    this.assetsSub?.unsubscribe();
  }

  setTab(tab: OwnerActionTab): void {
    this.activeTab.set(tab);
    this.actionError.set(null);
  }

  onSpaceFilterChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedSpaceId.set(value || 'all');
    this.actionError.set(null);
  }

  spaceLabel(booking: OwnerBookingRow): string {
    return booking.spaceName?.trim()
      || this.spaceNameById().get(booking.spaceId)
      || 'Espacio';
  }

  guestLabel(booking: OwnerBookingRow): string {
    return booking.guestName?.trim()
      || booking.userLegalName?.trim()
      || 'Cliente';
  }

  formatDate(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }

  shiftLabel(booking: OwnerBookingRow): string {
    return `${booking.startTime}–${booking.endTime}`;
  }

  rentalTotalEuros(booking: OwnerBookingRow): number {
    return rentalCentsFromBooking(booking) / 100;
  }

  ownerSignalEuros(booking: OwnerBookingRow): number {
    return ownerBalanceEurosFromBooking(booking);
  }

  showOwnerSignal(booking: OwnerBookingRow): boolean {
    return !isFullPaymentBooking(booking) && this.ownerSignalEuros(booking) > 0;
  }

  isStripeFullPayment(booking: OwnerBookingRow): boolean {
    return isFullPaymentBooking(booking);
  }

  hasSecurityDepositReceived(booking: OwnerBookingRow): boolean {
    return isSecurityDepositSecured(booking);
  }

  needsDepositReceiptReview(booking: OwnerBookingRow): boolean {
    return booking.securityDepositStatus === 'submitted'
      && (booking.securityDepositMethod === 'transfer' || booking.securityDepositMethod === 'bizum')
      && !!booking.securityDepositReceiptUrl;
  }

  pendingCashDeposit(booking: OwnerBookingRow): boolean {
    return booking.securityDepositStatus === 'submitted'
      && booking.securityDepositMethod === 'cash'
      && !booking.securityDepositReceivedAt;
  }

  async confirmSubmittedDeposit(booking: OwnerBookingRow): Promise<void> {
    if (!booking.id) return;
    await this.runAction(
      booking.id,
      () => this.bookingService.ownerConfirmSecurityDeposit(booking.id),
      'No se pudo validar la fianza. Inténtalo de nuevo.',
    );
  }

  openSecurityDepositReceipt(url: string | undefined): void {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  depositMethodLabel(booking: OwnerBookingRow): string | null {
    const m = booking.securityDepositReceivedMethod ?? booking.securityDepositMethod;
    if (!m || m === 'card_hold') {
      return m === 'card_hold' ? SECURITY_DEPOSIT_METHOD_LABELS.card_hold : null;
    }
    return SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS[m as SecurityDepositReceivedMethod];
  }

  depositSkippedAtActivation(booking: OwnerBookingRow): boolean {
    return !!booking.securityDepositSkippedByOwnerAt;
  }

  spaceRequiresDeposit(booking: OwnerBookingRow): boolean {
    const space = this.spaces().find(s => s.id === booking.spaceId);
    return (space?.securityDeposit ?? 0) > 0;
  }

  canActivateNormally(booking: OwnerBookingRow): boolean {
    return this.hasSecurityDepositReceived(booking) || !this.spaceRequiresDeposit(booking);
  }

  openDepositMethodModal(booking: OwnerBookingRow): void {
    if (this.hasSecurityDepositReceived(booking)) return;
    this.depositModalBooking.set(booking);
    this.depositMethodSelection.set(
      this.pendingCashDeposit(booking) ? 'cash' : null,
    );
    this.actionError.set(null);
  }

  closeDepositMethodModal(): void {
    if (this.actionBusyId()) return;
    this.depositModalBooking.set(null);
    this.depositMethodSelection.set(null);
  }

  selectDepositMethod(method: SecurityDepositReceivedMethod): void {
    this.depositMethodSelection.set(method);
  }

  async confirmDepositMethod(): Promise<void> {
    const booking = this.depositModalBooking();
    const method = this.depositMethodSelection();
    if (!booking?.id || !method) return;

    await this.runAction(
      booking.id,
      () => this.bookingService.ownerMarkSecurityDepositReceived(booking.id, method),
      'No se pudo registrar la fianza. Inténtalo de nuevo.',
    );
    if (!this.actionError()) {
      this.closeDepositMethodModal();
    }
  }

  openReceipt(url: string | undefined): void {
    if (!url?.trim()) return;
    const isPdf = /\.pdf($|\?)/i.test(url) || url.includes('application%2Fpdf');
    this.previewIsPdf.set(isPdf);
    this.previewUrl.set(url);
  }

  closeReceiptPreview(): void {
    this.previewUrl.set(null);
    this.previewIsPdf.set(false);
  }

  private async runAction(bookingId: string, fn: () => Promise<void>, errorMsg: string): Promise<void> {
    if (this.actionBusyId()) return;
    this.actionBusyId.set(bookingId);
    this.actionError.set(null);
    try {
      await fn();
    } catch {
      this.actionError.set(errorMsg);
    } finally {
      this.actionBusyId.set(null);
    }
  }

  confirmBooking(booking: OwnerBookingRow): Promise<void> {
    if (!booking.id) return Promise.resolve();
    return this.runAction(
      booking.id,
      () => this.bookingService.ownerConfirmPayment(booking.id),
      'No se pudo confirmar la reserva. Inténtalo de nuevo.',
    );
  }

  openRejectModal(booking: OwnerBookingRow): void {
    this.rejectNote.set('');
    this.confirmModal.set({ kind: 'reject', booking });
    this.actionError.set(null);
  }

  openReturnDepositModal(booking: OwnerBookingRow): void {
    this.confirmModal.set({ kind: 'returnDeposit', booking });
    this.actionError.set(null);
  }

  openCaptureDepositModal(booking: OwnerBookingRow): void {
    this.captureAmountEur.set(((booking.depositCents ?? 0) / 100).toFixed(2));
    this.confirmModal.set({ kind: 'captureDeposit', booking });
    this.actionError.set(null);
  }

  onCaptureAmountInput(event: Event): void {
    this.captureAmountEur.set((event.target as HTMLInputElement).value);
  }

  openActivateWithoutDepositModal(booking: OwnerBookingRow): void {
    this.confirmModal.set({ kind: 'activateWithoutDeposit', booking });
    this.actionError.set(null);
  }

  openCompleteEventModal(booking: OwnerBookingRow): void {
    this.confirmModal.set({ kind: 'completeEvent', booking });
    this.actionError.set(null);
  }

  closeConfirmModal(): void {
    if (this.actionBusyId()) return;
    this.confirmModal.set(null);
    this.rejectNote.set('');
  }

  onRejectNoteInput(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value;
    this.rejectNote.set(value.slice(0, this.rejectNoteMaxLength));
  }

  async executeConfirmModal(): Promise<void> {
    const modal = this.confirmModal();
    if (!modal?.booking.id) return;

    const { kind, booking } = modal;
    const errorByKind: Record<ConfirmModalKind, string> = {
      reject: 'No se pudo devolver la reserva a formalización. Inténtalo de nuevo.',
      returnDeposit: 'No se pudo registrar la devolución de la fianza. Inténtalo de nuevo.',
      captureDeposit: 'No se pudo cobrar la fianza. Inténtalo de nuevo.',
      activateWithoutDeposit: 'No se pudo activar la reserva. Inténtalo de nuevo.',
      completeEvent: 'No se pudo finalizar el evento. Inténtalo de nuevo.',
    };

    await this.runAction(
      booking.id,
      () => {
        switch (kind) {
          case 'reject':
            return this.bookingService.ownerRejectVerification(
              booking.id,
              this.rejectNote().trim() || undefined,
            );
          case 'returnDeposit':
            return booking.securityDepositMethod === 'card_hold'
              ? this.bookingService.ownerReleaseDepositCardHold(booking.id)
              : this.bookingService.ownerReturnSecurityDeposit(booking.id);
          case 'captureDeposit':
            return this.bookingService.ownerCaptureDepositCardHold(
              booking.id,
              this.captureAmountCents(),
            );
          case 'activateWithoutDeposit':
            return this.bookingService.ownerActivateBooking(booking.id, {
              skipDepositConfirmation: true,
            });
          case 'completeEvent':
            return this.bookingService.ownerCompleteEventWithoutDeposit(booking.id);
        }
      },
      errorByKind[kind],
    );
    if (!this.actionError()) {
      this.closeConfirmModal();
    }
  }

  activateBooking(booking: OwnerBookingRow): Promise<void> {
    if (!booking.id) return Promise.resolve();
    if (!this.canActivateNormally(booking)) {
      this.openActivateWithoutDepositModal(booking);
      return Promise.resolve();
    }
    return this.runAction(
      booking.id,
      () => this.bookingService.ownerActivateBooking(booking.id),
      'No se pudo activar la reserva. Inténtalo de nuevo.',
    );
  }

}
